/**
 * Les rôles de CDT se nomment `cdt-*` (RG-acces-roles-nommes-cdt, cadrage 2026-031). Le temps du
 * passage (décision 02), l'ancien nom vaut le nouveau : un jeton, un compte ou une réponse de l'API
 * qui le porte encore est lu sous le nouveau nom, comme le fait l'API (`UserRoleEnum.canonical`).
 *
 * Ce que l'application écrit à l'API part sous l'**ancien** nom : l'API qui ne connaît que lui
 * (avant vanex-cdt-back#338) comme celle qui connaît les deux l'acceptent, si bien que
 * l'application peut être livrée avant l'API. À retirer, avec l'équivalence, à l'étape 5 du
 * cadrage, quand plus rien ne porte d'ancien nom.
 *
 * `cdt-process-manager` garde son nom ; `admin` et `flotte-lire-cdt` ne sont pas des rôles de CDT.
 */
const CURRENT_BY_FORMER: Readonly<Record<string, string>> = Object.freeze({
  'administration-du-personnel': 'cdt-administration-du-personnel',
  'assistant-de-production': 'cdt-assistant-de-production',
  'responsable-exploitation': 'cdt-responsable-exploitation',
  'directeur-rh': 'cdt-directeur-rh',
  'responsable-rh': 'cdt-responsable-rh',
  'responsable-paie': 'cdt-responsable-paie',
  'directeur-agence': 'cdt-directeur-agence',
  'directeur-admin-financier': 'cdt-directeur-admin-financier',
  'directeur-general': 'cdt-directeur-general',
  'directeur-production': 'cdt-directeur-production',
});

const FORMER_BY_CURRENT: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(CURRENT_BY_FORMER).map(([former, current]) => [current, former]),
  ),
);

/** Le nom d'un rôle tel que CDT le lit : l'ancien nom ramené au nouveau, tout autre tel quel. */
export function canonicalRole<T extends string = string>(role: string): T {
  return (Object.hasOwn(CURRENT_BY_FORMER, role) ? CURRENT_BY_FORMER[role] : role) as T;
}

/** Une liste de rôles lue sous les nouveaux noms, chacun une fois. */
export function canonicalRoles<T extends string = string>(roles: readonly string[]): T[] {
  return [...new Set(roles.map((role) => canonicalRole<T>(role)))];
}

/** Le nom sous lequel un rôle s'écrit à l'API pendant le passage : l'ancien, s'il en a un. */
export function formerRoleName<T extends string = string>(role: string): T {
  return (Object.hasOwn(FORMER_BY_CURRENT, role) ? FORMER_BY_CURRENT[role] : role) as T;
}

export function formerRoleNames<T extends string = string>(roles: readonly string[]): T[] {
  return roles.map((role) => formerRoleName<T>(role));
}
